'use strict';

const trackingUtils = require('od-modules/utils/tracking.utils');
const BaseView = require('components-framework/view/base.view');
const withRenderedMain = require('components-framework/view/with_rendered_main');
const withCachedSelectors = require('components-framework/view/with_cached_selectors');
const Fetcher = require('od-modules/utils/fetcher');
const itineraryStatusUtils = require('../utils/utils');
const CONST = require('../utils/constants');
const tracking = require('../../../../webapp/static-content/_versionedByRelease_/js/tracking/itinerary_status');
const bookingInfo = require('components/user_area/booking_info');
const {preloadMmbBase} = require('components/user_area/booking_info/mmb.preload');

const getArrivalCity = (booking = {}) => {
    const {itineraryBookings = {}} = booking;
    const {bookingItinerary = {}} = itineraryBookings;
    const {arrival = {}} = bookingItinerary;
    return arrival.cityName;
};

module.exports = withRenderedMain(withCachedSelectors(BaseView.extend({
    selectors: {
        changedFlights: '.changed_flights',
        changedFlightsInfoPopupWrapper: '.changed_flights_info_popup_wrapper',
        changedFlightsInfoPopup: '.itinerary_changed_info_popup',
        changedFlightsInfoPopupClose: '.itinerary_changed_info_popup_close',
        changedFlightsInfoPopupContent: '.itinerary_changed_info_popup_content',
        contactAirlineGuideButton: '.contact_airline_guide_button',
        changedFlightsLayer: '.status_change_layer',
    },
    events: {
        'tap .itinerary_status_cta': 'onCtaClick',
        'tap .itinerary_changed_info_popup_close': 'hidePopup',
        'tap .itinerary_status_layer_open': 'openStatusChangeLayer',
        'tap .status_change_layer_back_arrow': 'closeStatusChangeLayer',
    },

    initialize: function({
        email = '',
        bookingId = '',
        changedPNRs = {},
        mmbLink = CONST.LINKS.HELP_CENTRE,
    }) {
        this.email = email;
        this.bookingId = bookingId;
        this.changedPNRs = changedPNRs;
        this.mmbLink = mmbLink;
        this.changedPNRsRequestsStatus = {};
        this.airlineList = itineraryStatusUtils.buildAirlinesMap(this.keys);

        preloadMmbBase();

        this.renderLoader();

        const bookingInfo = Odigeo.Utils.parseJson(Odigeo.Session.get('bookingInfo'));
        if (!Odigeo._.isNull(bookingInfo)) {
            this.bookingInfo = bookingInfo;
        }

        if (!Odigeo.AB.isFlightStatusDesktopGds) {
            this.render();
        } else {
            this.loadPnrStatus();
        }
    },

    setMmbLink(mmbLink) {
        this.mmbLink = mmbLink;
    },

    renderLoader() {
        this.$el.html(this.templates.itineraryChangedAlertLoader());
    },

    loadPnrStatus() {
        const request = {
            bookingId: this.bookingId,
            pnr: Object.keys(this.changedPNRs),
        };
        Fetcher.post(CONST.ENDPOINTS.checkRefundWithMultiplePnr, request).json()
            .then((changedPNRsRequestsStatus) => {
                this.changedPNRsRequestsStatus = changedPNRsRequestsStatus;
                this.render();
            })
            .catch(() => {});
    },

    render() {
        if (Odigeo.Utils.isMobile()) {
            const alertTitle = this.areAllPNRsBankruptcy(this.changedPNRs)
                ? this.keys.itineraryStatusCeasedOperations
                : this.keys.itineraryStatusCancelledByAirline;
            const changedSections = this.getChangedSectionsV2Mobile();
            this.$el.html(this.templates.itineraryChangedAlertV2({
                changedSections,
                keys: {
                    ...this.keys,
                    title: alertTitle,
                },
            }));
            this.initLayer();
        } else {
            const changedSections = this.getChangedSections();

            if (!Odigeo._.isEmpty(changedSections)) {
                this.$el.html(this.templates.itineraryChangedAlert({
                    changedSections,
                    popup: this.getPopupContent(),
                    keys: {
                        title: this.keys.itineraryStatusAlertTitle,
                    },
                }));
            }
        }

        if (Odigeo.History.getPageNameFromFragment() === 'tripdetails') {
            Odigeo.trigger(`${this.id}:onLoadWidgetTripDetailsTracking`, {
                bookingInfo: this.bookingInfo,
                changedPNRs: this.changedPNRs,
                airlineList: this.airlineList,
            });
        }
        this.updateDomSelectorsToCache();
    },

    initLayer() {
        const cards = this.getLayerCards();

        this.$el.find(this.selectors.changedFlightsLayer).html(this.templates.itineraryChangedLayer({
            keys: this.keys,
            bookingId: this.bookingId,
            cards: cards,
        }));
    },

    /**
     *
     * @return {string} a card template for each changedPNR.
     */
    getLayerCards() {
        let changedSections = '';

        for (const pnr in this.changedPNRs) {
            if (Object.prototype.hasOwnProperty.call(this.changedPNRs, pnr)) {
                const changedPNRData = this.changedPNRs[pnr];
                changedSections += this.getLayerCardsContent(changedPNRData, pnr);
            }
        }
        return changedSections;
    },

    getLayerCardsContent(changedPNRData, pnr) {
        let template = this.templates.itineraryChangedCard;
        const changedSections = this.getChangedSectionsV2Mobile({changedPNRData});
        const carrierGroup = this.getCarrierGroupByPnr(pnr) || CONST.AIRLINES.GROUPS.UNHANDLED;
        const data = CONST.layer.getData(carrierGroup, this.keys);
        const details = this.getChangedSectionDetailsContent(pnr, carrierGroup, data);
        const areAllPNRsBankruptcy = this.areAllPNRsBankruptcy({changedPNRData});
        const isOnePNRChanged = (Object.keys(this.changedPNRs).length === 1);
        const isCarrierLowCost = carrierGroup === CONST.AIRLINES.GROUPS.FR || carrierGroup === CONST.AIRLINES.GROUPS.VY;
        const isExpanderShown = areAllPNRsBankruptcy || !isOnePNRChanged || (isOnePNRChanged && isCarrierLowCost);
        const alertTitle = this.getLayerCardTitle(areAllPNRsBankruptcy, changedPNRData.sections);

        const templateData = {
            pnr,
            keys: {
                ...this.keys,
                title: alertTitle,
            },
            changedSections,
            details,
            isExpanderShown,
        };

        return template(templateData);
    },

    getChangedSections() {
        let changedSections = '';
        for (const pnr in this.changedPNRs) {
            if (Object.prototype.hasOwnProperty.call(this.changedPNRs, pnr)) {
                const changedPNRData = this.changedPNRs[pnr];
                const processedStatus = this.changedPNRsRequestsStatus[pnr];
                changedSections += !Odigeo._.isEmpty(processedStatus)
                    ? this.getActionRequestedContent(changedPNRData, pnr, Odigeo._.get(processedStatus, 'requestType'))
                    : this.getChangedSectionContent(changedPNRData, pnr);
            }
        }

        return changedSections;
    },

    /**
     *
     * @param {Object} changedPNRs - Object to get data from.
     * @return {string} with the template of all the changed sections
     */
    getChangedSectionsV2Mobile(changedPNRs = this.changedPNRs) {
        let changedSectionsTemplate = '';
        const changedSectionsArray = Odigeo._.map(changedPNRs, 'sections').flat();
        changedSectionsArray.forEach((section, index) => {
            const isLastSection = index === changedSectionsArray.length - 1;
            changedSectionsTemplate += this.getSectionTemplate(section, isLastSection);
        }, this);
        return changedSectionsTemplate;
    },

    /**
     *
     * @param {Object} section - with all the section data
     * @param {boolean} isLast - tells whether this section is the last one to render and not add the dashed line below
     * @return {string} section template
     */
    getSectionTemplate(section, isLast) {
        const tripType = this.bookingInfo.itineraryBookings.bookingItinerary.tripType;
        const segments = this.bookingInfo.itineraryBookings.itinerarySegments;
        const template = this.templates.changedSection;
        const templateData = {
            carrierName: section.section.carrier.name,
            carrierCode: section.section.carrier.code,
            sectionName: itineraryStatusUtils.getSectionName(section, tripType, segments, this.keys),
            imagePath: Odigeo.UserArea.imagePath,
            from: section.section.from.cityIataCode,
            to: section.section.to.cityIataCode,
            date: Odigeo.Utils.formatDate(section.section.departureDate, 'ddd, mmm d'),
            lastSection: isLast,
            keys: this.keys,
        };
        return template(templateData);
    },

    getActionRequestedContent(changedPNRData, pnr, request) {
        let template = this.templates.itineraryChangedAlertActionRequested;

        const data = CONST.requestStatus.getData(request, this.keys);
        const templateData = {
            pnr,
            keys: data.keys,
            ...data.data,
        };

        return template(templateData);
    },

    getChangedSectionContent(changedPNRData, pnr) {
        let template = this.templates.itineraryChangedAlertSectionCancelled;
        const validatingCarrier = Odigeo._.get(changedPNRData, 'validatingCarrier.code');
        let carrierGroup = this.airlineList[validatingCarrier] || CONST.AIRLINES.GROUPS.UNHANDLED;

        const data = CONST.alert.getData(carrierGroup, this.keys);
        const templateData = {
            pnr,
            keys: data.keys,
            ...data.data,
        };
        return template(templateData);
    },

    getChangedSectionDetailsContent(pnr, carrierGroup, data) {
        let template;
        switch (carrierGroup) {
            case CONST.AIRLINES.GROUPS.FR:
            case CONST.AIRLINES.GROUPS.VY:
                template = this.templates.layerDetailsLowCost;
                break;
            default:
                template = this.templates.layerDetailsUnhandled;
                break;
        }

        return template({
            email: this.email,
            pnr,
            merchant: this.changedPNRs[pnr].merchant,
            keys: data.keys,
            ...data.data,
        });
    },

    getPopupContent() {
        return this.templates.itineraryChangedAlertPopup({});
    },

    openStatusChangeLayer() {
        this.$cachedSelectors.changedFlightsLayer.addClass('open');
        $('body').addClass('od-scroll-blocked');
    },

    closeStatusChangeLayer() {
        this.$cachedSelectors.changedFlightsLayer.removeClass('open');
        $('body').removeClass('od-scroll-blocked');
    },

    onCtaClick(e) {
        const $button = $(e.target).closest('.itinerary_status_cta');
        const action = $button.data('action');
        const pnr = $button.data('pnr');

        switch (action) {
            case CONST.CTA_ACTION.POPUP:
                this.triggerPopup(pnr, action);
                break;
            case CONST.CTA_ACTION.VOUCHER:
            case CONST.CTA_ACTION.REFUND:
                $button.addClass('loading disabled');
                this.processOfflineTask(action, pnr);
                break;
            case CONST.CTA_ACTION.GO_TO_MMB: {
                tracking.trackTripDetailsInvCanxWidgetOnClick(this.bookingInfo, this.changedPNRs, this.airlineList, action);
                this._goToMmbPageFromItineraryStatus();
                break;
            }
            case CONST.CTA_ACTION.GO_TO_URL: {
                const url = $button.data('url');
                itineraryStatusUtils.openExternalLink(url);
                tracking.trackTripDetailsOnPopupClicks(this.bookingInfo, this.changedPNRs, this.airlineList);
                break;
            }
            default:break;
        }
    },

    _goToMmbPageFromItineraryStatus: function() {
        const booking = this.bookingInfo;
        const {mail, name, lastNames} = booking.buyer;
        const bookingId = booking.bookingBasicInfo.id;
        const params = Odigeo._.isUndefined(bookingId) ? {} : {email: `${mail}`, bookingId: `${bookingId}`, name: `${name} ${lastNames}`};
        bookingInfo.mail = mail;
        bookingInfo.bookingId = bookingId;
        bookingInfo.destinationCity = getArrivalCity(booking);
        const label = `manage_my_booking_widget_click_pg:mytrips_id:${bookingId}`;
        trackingUtils.trackEvent('manage_my_booking_widget', label, 'mytrips');
        Odigeo.Router.navigate('managemybooking', {...params, params: `token=${bookingInfo.token}`});
    },

    processOfflineTask(action, pnr) {
        const request = {
            bookingId: this.bookingId,
            brand: itineraryStatusUtils.getBrand(),
            site: itineraryStatusUtils.getSite(),
            pnr,
            requestType: action,
            comments: Odigeo.Utils.replaceCmsKeyArgs(CONST.COMMENT[action], pnr, this.bookingId),
        };

        Fetcher.post(CONST.ENDPOINTS.createRefundWithPnr, request).json()
            .then((response) => {
                this.changedPNRsRequestsStatus[pnr] = Odigeo._.get(response, 'requestType', action);
                this.fillPopupWithFeedback(pnr, true);
            })
            .catch(() => {
                this.fillPopupWithFeedback(pnr, false);
            }).then(() => {
                setTimeout(function() {
                    this.hidePopup();
                    this.render();
                }.bind(this), 2000);
            });
    },

    fillPopupWithFeedback(pnr, success = false) {
        const carrierGroup = this.getCarrierGroupByPnr(pnr);
        const data = CONST.popup.getData(carrierGroup, this.keys);
        const template = this.templates.itineraryChangedAlertPopupContentFeedback;
        const status = success ? 'success' : 'error';
        const feedbackContent = template({
            keys: data.keys.feedback[status],
            ...data.data.feedback[status],
        });
        this.fillPopup(feedbackContent);
    },

    triggerPopup(pnr, action) {
        const carrierGroup = this.getCarrierGroupByPnr(pnr);
        const data = CONST.popup.getData(carrierGroup, this.keys);
        const popupBodyContent = this.getPopupBodyContent(pnr, carrierGroup, data);

        if (popupBodyContent) {
            this.fillPopup(popupBodyContent);
            this.togglePopup(true);
        }
        tracking.trackTripDetailsInvCanxWidgetOnClick(this.bookingInfo, this.changedPNRs, this.airlineList, action);
    },

    fillPopup(popupBodyContent) {
        this.$cachedSelectors.changedFlightsInfoPopupContent.html(popupBodyContent);
    },

    getPopupBodyContent(pnr, carrierGroup, data) {
        let template;
        switch (carrierGroup) {
            case CONST.AIRLINES.GROUPS.FR:
            case CONST.AIRLINES.GROUPS.VY:
                template = this.templates.itineraryChangedAlertPopupContentLowCost;
                break;
            case CONST.AIRLINES.GROUPS.REFUND:
            case CONST.AIRLINES.GROUPS.VOUCHER:
                template = this.templates.itineraryChangedAlertPopupContentGds;
                break;
            default: break;
        }

        return template({
            email: this.email,
            pnr,
            merchant: this.changedPNRs[pnr].merchant,
            keys: data.keys,
            ...data.data,
        });
    },

    hidePopup() {
        this.togglePopup(false);
    },

    togglePopup(show) {
        this.$cachedSelectors.changedFlightsInfoPopup.toggleClass('open', show);
    },

    getLayerCardTitle(areAllPNRsBankruptcy, sections) {
        let alertTitle;
        if (areAllPNRsBankruptcy) {
            alertTitle = this.keys.itineraryStatusCeasedOperations;
        } else {
            alertTitle = (Object.keys(sections).length === 1)
                ? this.keys.flightCancelled
                : this.keys.itineraryStatusCancelledByAirline;
        }
        return alertTitle;
    },

    getCarrierGroupByPnr(pnr) {
        const changedPNR = this.changedPNRs[pnr];
        const validatingCarrier = Odigeo._.get(changedPNR, 'validatingCarrier.code');
        return this.airlineList[validatingCarrier];
    },

    getAirlineList() {
        return this.airlineList;
    },

    areAllPNRsBankruptcy(data) {
        let bankruptcyPNRs = 0;

        for (const changedPNRData of Object.values(data)) {
            const {validatingCarrier: {code}} = changedPNRData;
            const carrierGroup = this.airlineList[code] || CONST.AIRLINES.GROUPS.UNHANDLED;
            if (carrierGroup === CONST.AIRLINES.GROUPS.BANKRUPTCY) {
                bankruptcyPNRs++;
            }
        }

        return bankruptcyPNRs === Object.keys(this.changedPNRs).length;
    },
})));
